import { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  InputAdornment,
  Button,
  Alert
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import InviteTable from '../../components/company/InviteTable';
import InviteUserModal from '../../components/company/InviteUserModal';
import { fetchInvites } from '../../redux/slices/inviteSlice';
import { useHasPermission } from '../../hooks/usePermission';
import ForbiddenPage from '../ForbiddenPage';
import PermissionButton from '../../components/common/PermissionButton';

/**
 * Invites Page
 * 
 * Company owner can view and manage all sent invites
 * Phase 4 M3: Invite Management
 */
const InvitesPage = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  
  const { 
    invites, 
    invitesTotal, 
    invitesPage, 
    invitesLimit, 
    invitesLoading,
    invitesError 
  } = useSelector((state) => state.invite);
  const { user: currentUser } = useSelector((state) => state.auth);
  
  // Permission-based guard (replaces userType guard)
  const hasPermission = useHasPermission('invites.view');
  
  // Check if user is system admin (for company filter)
  const isSystemAdmin = !currentUser?.companyId;

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [companyFilter, setCompanyFilter] = useState('all'); // 'all' | companyId
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  
  // Extract unique companies from invites (for system admin filter dropdown)
  const companies = useMemo(() => {
    if (!isSystemAdmin || !invites || invites.length === 0) return [];
    
    const companyMap = new Map();
    invites.forEach(invite => {
      if (invite.companyId && invite.companyId._id) {
        const companyId = invite.companyId._id;
        const companyName = invite.companyId.companyName || 'Bilinmeyen Şirket';
        if (!companyMap.has(companyId)) {
          companyMap.set(companyId, {
            _id: companyId,
            name: companyName
          });
        }
      }
    });
    
    return Array.from(companyMap.values()).sort((a, b) => 
      a.name.localeCompare(b.name, 'tr')
    );
  }, [invites, isSystemAdmin]);

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 500); // 500ms delay

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Fetch invites on mount & filter change
  useEffect(() => {
    if (hasPermission) {
      const params = {
        search: debouncedSearch,
        status: statusFilter,
        page: invitesPage,
        limit: invitesLimit
      };
      
      // System admin can filter by companyId
      if (isSystemAdmin && companyFilter && companyFilter !== 'all') {
        params.companyId = companyFilter;
      }
      
      dispatch(fetchInvites(params));
    }
  }, [dispatch, debouncedSearch, statusFilter, companyFilter, invitesPage, invitesLimit, hasPermission, isSystemAdmin]);

  // Handle search
  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  // Handle status filter
  const handleStatusFilter = (e) => {
    setStatusFilter(e.target.value);
  };

  // Handle invite new user
  const handleInviteUser = () => {
    setInviteModalOpen(true);
  };

  // Handle modal close and refresh data
  const handleModalClose = () => {
    setInviteModalOpen(false);
    // Refresh invites list after modal closes
    const params = {
      search: debouncedSearch,
      status: statusFilter,
      page: invitesPage,
      limit: invitesLimit
    };
    
    if (isSystemAdmin && companyFilter && companyFilter !== 'all') {
      params.companyId = companyFilter;
    }
    
    dispatch(fetchInvites(params));
  };

  // Handle back button
  const handleBack = () => {
    history.push('/users');
  };

  // Permission-based guard (replaces userType guard)
  if (!hasPermission) {
    return <ForbiddenPage />;
  }

  return (
      <Container maxWidth="xl">
        {/* Back Button */}
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          sx={{ mb: 3 }}
        >
          Geri Dön
        </Button>

        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Box>
            <Typography variant="h4" fontWeight="bold">
              {isSystemAdmin ? 'Tüm Davetler' : 'Gönderilen Davetler'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {isSystemAdmin 
                ? 'Sistemdeki tüm davetleri görüntüleyin ve yönetin' 
                : 'Şirketinize gönderdiğiniz tüm davetleri görüntüleyin ve yönetin'}
            </Typography>
          </Box>
          <PermissionButton
            permission="invites.send"
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleInviteUser}
          >
            Yeni Davet
          </PermissionButton>
        </Box>

        {/* Error Alert */}
        {invitesError && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {invitesError}
          </Alert>
        )}

        {/* Filters */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            {/* Search */}
            <TextField
              placeholder="Email ile ara..."
              value={searchTerm}
              onChange={handleSearch}
              sx={{ flex: 1, minWidth: 300 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />

            {/* Company Filter (System Admin only) */}
            {isSystemAdmin && (
              <FormControl sx={{ minWidth: 200 }}>
                <InputLabel>Şirket</InputLabel>
                <Select
                  value={companyFilter}
                  label="Şirket"
                  onChange={(e) => setCompanyFilter(e.target.value)}
                >
                  <MenuItem value="all">Tüm Şirketler</MenuItem>
                  {companies.map((company) => (
                    <MenuItem key={company._id} value={company._id}>
                      {company.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {/* Status Filter */}
            <FormControl sx={{ minWidth: 200 }}>
              <InputLabel>Durum</InputLabel>
              <Select
                value={statusFilter}
                label="Durum"
                onChange={handleStatusFilter}
              >
                <MenuItem value="">Tümü</MenuItem>
                <MenuItem value="pending">Beklemede</MenuItem>
                <MenuItem value="accepted">Kabul Edildi</MenuItem>
                <MenuItem value="expired">Süresi Doldu</MenuItem>
                <MenuItem value="cancelled">İptal Edildi</MenuItem>
                <MenuItem value="rejected">Reddedildi</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Paper>

        {/* Invite Table */}
        <InviteTable
          invites={invites}
          total={invitesTotal}
          page={invitesPage}
          limit={invitesLimit}
          loading={invitesLoading}
        />

        {/* Invite User Modal */}
        <InviteUserModal 
          open={inviteModalOpen} 
          onClose={handleModalClose}
        />
      </Container>
  );
};

export default InvitesPage;

